import { useEffect, useState, useRef } from 'react';
import { TrendingDown, Building2, Stethoscope } from 'lucide-react';
import { aboutIndia } from '../mockData';

const iconMap = {
  TrendingDown,
  Building2,
  Stethoscope
};

const TiltCard = ({ item, index, isVisible }) => {
  const cardRef = useRef(null);
  const [rotation, setRotation] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;

    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateX = ((y - centerY) / centerY) * -10; // Max 10 deg rotation
    const rotateY = ((x - centerX) / centerX) * 10;

    setRotation({ x: rotateX, y: rotateY });
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    setRotation({ x: 0, y: 0 });
  };

  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  const IconComponent = iconMap[item.icon];

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
      className={`relative h-full transition-all duration-1000 delay-${index * 100} ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
      style={{
        perspective: '1000px',
        transformStyle: 'preserve-3d',
      }}
    >
      <div
        className={`relative h-full bg-white rounded-2xl p-8 border border-gray-100 shadow-lg transition-all duration-200 ease-out`}
        style={{
          transform: isHovering
            ? `rotateX(${rotation.x}deg) rotateY(${rotation.y}deg) scale3d(1.05, 1.05, 1.05)`
            : 'rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)',
        }}
      >
        {/* Gradient Overlay for 3D depth */}
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/50 to-transparent opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-300" />

        {/* Abstract Background Shape */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-brand-blue/10 to-brand-teal/10 rounded-bl-full opacity-50"
          style={{ transform: 'translateZ(20px)' }}
        />

        <div className="relative" style={{ transform: 'translateZ(30px)' }}>
          <div className="w-16 h-16 bg-gradient-to-br from-brand-blue to-brand-teal rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-brand-blue/20">
            <IconComponent className="h-8 w-8 text-white" />
          </div>

          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            {item.title}
          </h3>

          <p className="text-gray-600 leading-relaxed">
            {item.description}
          </p>
        </div>
      </div>
    </div>
  );
};

export const AboutIndia = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    const element = document.getElementById('about');
    if (element) observer.observe(element);

    return () => {
      if (element) observer.unobserve(element);
    };
  }, []);

  return (
    <section id="about" className="py-20 bg-gray-50 overflow-hidden">
      <div className="container mx-auto px-6">
        <div
          className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
        >
          <div className="inline-block px-4 py-2 bg-brand-blue/10 text-brand-navy rounded-full text-sm font-medium mb-4 animate-pulse">
            Why India?
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 bg-clip-text text-transparent bg-gradient-to-r from-brand-navy to-brand-blue">
            India: A Global Medical Hub
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Combining ancient wisdom with modern medical excellence, India has become the preferred destination for international patients
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 perspective-1000">
          {aboutIndia.map((item, index) => (
            <TiltCard key={item.id} item={item} index={index} isVisible={isVisible} />
          ))}
        </div>
      </div>
    </section>
  );
};